/**
 * base64url and UTF-8, without depending on Node or on the DOM.
 *
 * The SDK runs in Node, in edge runtimes, in a worker and in a browser, and the
 * licence string has to decode identically in all four. `atob`/`btoa` exist
 * everywhere modern but are byte-oriented and mangle anything above U+00FF, so
 * text goes through `TextEncoder`/`TextDecoder` first and only bytes are
 * base64'd. The tables below are the whole implementation, which is smaller
 * than the runtime detection needed to use `Buffer` where it happens to exist.
 */

const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_";
const LOOKUP = /* @__PURE__ */ (() => {
  const t = new Int8Array(128).fill(-1);
  for (let i = 0; i < ALPHABET.length; i++) t[ALPHABET.charCodeAt(i)] = i;
  // Accept standard base64 as well, so a licence that survived a round trip
  // through a system that rewrote the alphabet still reads.
  t["+".charCodeAt(0)] = 62;
  t["/".charCodeAt(0)] = 63;
  return t;
})();

export function bytesToBase64Url(bytes: Uint8Array): string {
  let out = "";
  let i = 0;
  for (; i + 2 < bytes.length; i += 3) {
    const n = (bytes[i]! << 16) | (bytes[i + 1]! << 8) | bytes[i + 2]!;
    out += ALPHABET[(n >>> 18) & 63]! + ALPHABET[(n >>> 12) & 63]! + ALPHABET[(n >>> 6) & 63]! + ALPHABET[n & 63]!;
  }
  const rest = bytes.length - i;
  if (rest === 1) {
    const n = bytes[i]! << 16;
    out += ALPHABET[(n >>> 18) & 63]! + ALPHABET[(n >>> 12) & 63]!;
  } else if (rest === 2) {
    const n = (bytes[i]! << 16) | (bytes[i + 1]! << 8);
    out += ALPHABET[(n >>> 18) & 63]! + ALPHABET[(n >>> 12) & 63]! + ALPHABET[(n >>> 6) & 63]!;
  }
  return out;
}

/** Throws on anything that is not base64/base64url. Padding is optional. */
export function base64UrlToBytes(text: string): Uint8Array {
  let end = text.length;
  while (end > 0 && text[end - 1] === "=") end--;
  const chars: number[] = [];
  for (let i = 0; i < end; i++) {
    const code = text.charCodeAt(i);
    const value = code < 128 ? LOOKUP[code]! : -1;
    if (value < 0) throw new Error(`not base64: ${JSON.stringify(text[i])} at ${i}`);
    chars.push(value);
  }
  // 4 base64 characters carry 3 bytes; a group of 1 is never valid.
  if (chars.length % 4 === 1) throw new Error("not base64: truncated");
  const out = new Uint8Array(Math.floor((chars.length * 3) / 4));
  let o = 0;
  let i = 0;
  for (; i + 3 < chars.length; i += 4) {
    const n = (chars[i]! << 18) | (chars[i + 1]! << 12) | (chars[i + 2]! << 6) | chars[i + 3]!;
    out[o++] = (n >>> 16) & 255;
    out[o++] = (n >>> 8) & 255;
    out[o++] = n & 255;
  }
  const rest = chars.length - i;
  if (rest === 2) {
    out[o++] = ((chars[i]! << 18) | (chars[i + 1]! << 12)) >>> 16;
  } else if (rest === 3) {
    const n = (chars[i]! << 18) | (chars[i + 1]! << 12) | (chars[i + 2]! << 6);
    out[o++] = (n >>> 16) & 255;
    out[o++] = (n >>> 8) & 255;
  }
  return out;
}

const encoder = /* @__PURE__ */ new TextEncoder();
const decoder = /* @__PURE__ */ new TextDecoder("utf-8", { fatal: true });

export const utf8ToBytes = (text: string): Uint8Array => encoder.encode(text);
export const bytesToUtf8 = (bytes: Uint8Array): string => decoder.decode(bytes);
